import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, Container, MetricCard, PageHeader, Select, Table, Tag } from "../../ui";
import { errorMessageOf, pageParams, scholarshipsApi } from "../../../api/scholarships";
import { useAcademicYears } from "../../../hooks/useAcademicYears";
import { SCHOLARSHIPS_KEY, SCHOLARSHIP_TYPES, TYPE_LABELS, countryName, stateOf } from "../model";

// Step 7 of the client's flow: how each scholarship's applications went, from submission to the awardees' follow-up. Counts only.

const reportParams = (filters) => {
  const { page, size, ...set } = pageParams(filters); // the report is not paged
  return set;
};

const useScholarshipReport = (filters) =>
  useQuery({
    queryKey: [...SCHOLARSHIPS_KEY, "report", filters],
    placeholderData: (previous) => previous,
    queryFn: async () => (await scholarshipsApi.get("staff/reports/scholarships", { params: reportParams(filters) })).data,
  });

const total = (rows, key) => rows.reduce((sum, r) => sum + (r[key] ?? 0), 0);
const count = (key, label) => ({ key, label, accessor: (r) => r[key], sortValue: (r) => r[key] });

export default function ScholarshipReportPage() {
  const [filters, setFilters] = useState({ academicYearId: "", type: "" });
  const [downloading, setDownloading] = useState(false);
  const { data: rows = [], isPending, isError } = useScholarshipReport(filters);
  const { data: academicYears = [] } = useAcademicYears({ includeFuture: true });
  const yearOptions = useMemo(() => academicYears.map((y) => ({ value: y.id, label: y.academicYearName })), [academicYears]);
  const setFilter = (name) => (e) => setFilters((prev) => ({ ...prev, [name]: e.target.value }));

  // The API's CSV escapes text a spreadsheet would run as a formula, so download it rather than building one here
  const download = async () => {
    setDownloading(true);
    try {
      const { data } = await scholarshipsApi.get("staff/reports/scholarships.csv", { params: reportParams(filters), responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `scholarships-report-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error("Failed to download the scholarships report:", err);
      toast.error(errorMessageOf(err, "Failed to download the report. Please try again."));
    } finally {
      setDownloading(false);
    }
  };

  const columns = [
    { key: "name", label: "Scholarship", accessor: (r) => r.name, render: (r) => <span className="font-medium text-slate-800">{r.name}</span> },
    { key: "type", label: "Type", accessor: (r) => TYPE_LABELS[r.type] ?? r.type, filter: "Any type" },
    { key: "state", label: "State", accessor: (r) => stateOf(r.state).label, render: (r) => <Tag color={stateOf(r.state).color}>{stateOf(r.state).label}</Tag> },
    { key: "academicYear", label: "Academic year", accessor: (r) => r.academicYear || "—" },
    { key: "sponsor", label: "Partner", accessor: (r) => r.sponsor ?? "—" },
    { key: "destination", label: "Country", accessor: (r) => countryName(r.destinationCountryCode) },
    count("submitted", "Submitted"),
    count("withdrawn", "Withdrawn"),
    count("inReview", "In review"),
    count("reviewed", "Reviewed"),
    count("awarded", "Awarded"),
    count("reserve", "Reserve"),
    count("notAwarded", "Not awarded"),
    count("withDg", "With the DG"),
    count("withMinister", "With the Minister"),
    count("returnedToHec", "Returned to HEC"),
    count("approved", "Approved"),
    count("rejected", "Rejected"),
    count("notTakenUp", "Granted, not admitted yet"),
    count("receivedByUniversity", "Admitted by a university"),
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader
        title="Scholarships report"
        subtitle="Applications, awards, approvals and the awardees' follow-up for each scholarship."
        actions={
          <Button icon={<ArrowDownTrayIcon className="h-4 w-4" />} onClick={download} loading={downloading} disabled={!rows.length}>
            Download CSV
          </Button>
        }
      />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <MetricCard title="Scholarships" value={rows.length} />
        <MetricCard title="Applications" value={total(rows, "submitted")} />
        <MetricCard title="Awarded" value={total(rows, "awarded")} />
        <MetricCard title="Approved" value={total(rows, "approved")} />
        <MetricCard title="Admitted" value={total(rows, "receivedByUniversity")} />
      </div>
      <Table
        columns={columns}
        data={rows}
        rowKey={(r) => r.scholarshipId}
        isLoading={isPending}
        exportable={false}
        emptyTitle={isError ? "Could not load the report. Please try again later." : "No scholarships match"}
        filters={
          <>
            <Select aria-label="Academic year" placeholder="All academic years" value={filters.academicYearId} onChange={setFilter("academicYearId")} options={yearOptions} className="w-full sm:w-56" />
            <Select aria-label="Type" placeholder="All types" value={filters.type} onChange={setFilter("type")} options={SCHOLARSHIP_TYPES} className="w-full sm:w-48" />
          </>
        }
      />
    </Container>
  );
}
